import React from 'react';
import { useStaticQuery, graphql, Link } from 'gatsby';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getReferenceUrl } from '../utils/utils';
import GenericLink from './GenericLink';

import { COUNTRY_CODE, DOMAIN_SUFFIX, HIDDEN_FEATURES } from '../constants';

import * as styles from './Footer.module.scss';

import AscenzaLogoSvg from '../assets/images/logos/ASCENZA_logo_white_rovensa-company.svg';
import RovensaLogoSvg from '../assets/images/logos/ROVENSA_white_well-balanced-agriculture.svg';
import Lisboa2020_1a from '../assets/images/logos/Lisboa2020_RGB_1a.png';
import Lisboa2020_1b from '../assets/images/logos/Lisboa2020_RGB_1b.png';
import Lisboa2020_1c from '../assets/images/logos/Lisboa2020_RGB_1c.png';
import PORLisboaLogoPng from '../assets/images/logos/logo_porlisboa.png';
import QRENLogoPng from '../assets/images/logos/qren-logo.png';
import FEDERLogoPng from '../assets/images/logos/FEDER-logo.png';

interface FooterProps {
  onClick: () => void;
}

const Footer = ({ onClick }: FooterProps): React.ReactElement => {
  const { t } = useTranslation();
  const data: any = useStaticQuery(
    graphql`
      {
        allSanityLegalDocumentation {
          totalCount
          nodes {
            id
            shortTitle
            slug {
              current
            }
          }
        }
        sanityWebsite {
          footer {
            navigationLinks {
              title
              url
              linkReference {
                ...LinkReference
              }
              icon {
                asset {
                  url
                }
              }
            }
            socialMedia {
              youtubeUrl
              facebookUrl
              twitterUrl
              linkedInUrl
              instagramUrl
            }
          }
        }
      }
    `,
  );

  const website = data.sanityWebsite || {};
  const socialMedia = website.footer.socialMedia;
  const navigationLinks = website.footer.navigationLinks;

  return (
    <>
      <footer className={styles.footer} onClick={onClick}>
        <div className={styles.topSection}>
          {navigationLinks.length > 0 && (
            <div className={styles.leftSideContent}>
              <section className={styles.section}>
                <span className={styles.title}>{t('footer.navigation_label', 'Navigation')}</span>
                <div className={styles.navigationLinks + ' ' + styles.border}>
                  <div className={styles.navigationLinksContainer}>
                    {navigationLinks.map((link, i) => (
                      <GenericLink
                        noStyle
                        className={styles.link}
                        key={i}
                        to={
                          link.linkReference
                            ? getReferenceUrl(
                                link.linkReference.__typename,
                                link.linkReference.slug.current,
                              )
                            : link.url
                        }
                      >
                        {link.icon && (
                          <img className={styles.linkIcon} src={link.icon.asset.url} alt="" />
                        )}
                        {link.title}
                      </GenericLink>
                    ))}
                  </div>
                </div>
              </section>
            </div>
          )}

          <div className={styles.rightSide}>
            {data.allSanityLegalDocumentation.totalCount > 0 && (
              <section className={styles.section}>
                <span className={styles.title}>
                  {t('footer.legal_documentation_label', 'Legal documentation')}
                </span>
                <div className={styles.legalDocumentation + ' ' + styles.border}>
                  {data.allSanityLegalDocumentation.nodes.map((legalDocument: any) => (
                    <GenericLink
                      noStyle
                      key={legalDocument.slug.current}
                      to={`/legalDocumentation/${legalDocument.slug.current}`}
                      className={styles.link}
                    >
                      {legalDocument.shortTitle}
                    </GenericLink>
                  ))}
                </div>
              </section>
            )}
          </div>

          <div className={styles.centerContent}>
            <div className={styles.logosContainer}>
              <a
                href="https://www.ascenza.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.logoFooter}
              >
                <img className={styles.logo} src={AscenzaLogoSvg} alt="Ascenza Logo" />
              </a>
              <a
                href="https://www.rovensa.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.logoFooter}
              >
                <img className={styles.logo} src={RovensaLogoSvg} alt="Rovensa Logo" />
              </a>
            </div>
          </div>
        </div>

        <span className={styles.copyright}>2020 &copy; All rights reserved</span>

        <div className={styles.divider}></div>

        {!!socialMedia &&
          (!!socialMedia.youtubeUrl ||
            !!socialMedia.twitterUrl ||
            !!socialMedia.linkedInUrl ||
            !!socialMedia.facebookUrl ||
            !!socialMedia.instagramUrl) && (
            <section className={styles.socialMediaSection}>
              <div className={styles.socialMedia}>
                {!!socialMedia.youtubeUrl && (
                  <a
                    className={styles.socialMediaLink}
                    href={socialMedia.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FontAwesomeIcon icon={['fab', 'youtube']} className={styles.icon} />
                  </a>
                )}
                {!!socialMedia.twitterUrl && (
                  <a
                    className={styles.socialMediaLink}
                    href={socialMedia.twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FontAwesomeIcon icon={['fab', 'twitter']} className={styles.icon} />
                  </a>
                )}
                {!!socialMedia.linkedInUrl && (
                  <a
                    className={styles.socialMediaLink}
                    href={socialMedia.linkedInUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FontAwesomeIcon icon={['fab', 'linkedin-in']} className={styles.icon} />
                  </a>
                )}
                {!!socialMedia.facebookUrl && (
                  <a
                    className={styles.socialMediaLink}
                    href={socialMedia.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FontAwesomeIcon icon={['fab', 'facebook-f']} className={styles.icon} />
                  </a>
                )}
                {!!socialMedia.instagramUrl && (
                  <a
                    className={styles.socialMediaLink}
                    href={socialMedia.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FontAwesomeIcon icon={['fab', 'instagram']} className={styles.icon} />
                  </a>
                )}
              </div>
            </section>
          )}
      </footer>

      {COUNTRY_CODE === 'pt' && (
        <div className={styles.projectsAndIncentives}>
          <Link
            to="/projects-and-incentives/projects"
            className={styles.projectsAndIncentivesLeftBlock}
          >
            <div className={styles.projectsAndIncentivesLogoContainer}>
              <img
                className={styles.projectsAndIncentivesLogo}
                src={Lisboa2020_1a}
                alt="Lisboa 2020 Logo"
              />
            </div>

            <div className={styles.projectsAndIncentivesLogoContainer}>
              <img
                className={styles.projectsAndIncentivesLogo}
                src={Lisboa2020_1b}
                alt="Portugal 2020 Logo"
              />
            </div>

            <div className={styles.projectsAndIncentivesLogoContainer}>
              <img
                className={styles.projectsAndIncentivesLogo}
                src={Lisboa2020_1c}
                alt="FEDER Logo"
              />
            </div>
          </Link>
          <Link
            to="/projects-and-incentives/incentives"
            className={styles.projectsAndIncentivesRightBlock}
          >
            <div className={styles.projectsAndIncentivesLogoContainer}>
              <img
                className={styles.projectsAndIncentivesLogo}
                src={PORLisboaLogoPng}
                alt="POR Lisboa Logo"
              />
            </div>
            <div className={styles.projectsAndIncentivesLogoContainer}>
              <img className={styles.projectsAndIncentivesLogo} src={QRENLogoPng} alt="QREN Logo" />
            </div>
            <div className={styles.projectsAndIncentivesLogoContainer}>
              <img
                className={styles.projectsAndIncentivesLogo}
                src={FEDERLogoPng}
                alt="FEDER Logo"
              />
            </div>
          </Link>
        </div>
      )}

      <div
        className={
          styles.moscaSection + ' ' + (COUNTRY_CODE === 'pt' ? styles.moscaSectionPt : null)
        }
      >
        <span className={styles.moscaLink}>
          Powered by{' '}
          <a
            className={styles.moscaLink}
            href={'https://moscadigital.pt/?utm_source=ascenza.' + DOMAIN_SUFFIX}
            target="_blank"
            rel="noopener noreferrer"
          >
            Mosca Digital
          </a>
        </span>
      </div>
    </>
  );
};

export default Footer;
